import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ToggleGroup,
  Select,
  SelectOption,
  Modal,
  Button,
  InputField,
  Checkbox,
} from '@ets/design-system';

import EditorManager from 'Editor/services/EditorManager/EditorManager';
import { closeModal } from 'App/ModalContext/utils';
import { useModalData } from 'App/ModalContext/ModalContext';

import styles from './TOCModal.module.scss';
import { toast } from 'utils/toast';
import { Logger } from '_common/services';

const TRANSLATIONS = {
  create: {
    header: 'INSERT_TABLE_OF_CONTENTS',
    submit: 'global.insert',
  },
  edit: {
    header: 'EDIT_TABLE_OF_CONTENTS',
    submit: 'global.edit',
  },
};

type OptionsConstructor<T extends string> = Record<
  T,
  { labelConstructor: (text: string) => string; labelId: string }
>;

const TAB_LEADER: OptionsConstructor<Exclude<Editor.Data.TabLeader, 'n'>> = {
  d: {
    labelConstructor: (text) => `.................   (${text})`,
    labelId: 'ROW_OF_DOTS',
  },
  h: {
    labelConstructor: (text) => `----------   (${text})`,
    labelId: 'ROW_OF_DASHED',
  },
  u: {
    labelConstructor: (text) => `__________   (${text})`,
    labelId: 'ROW_OF_UNDERSCORE',
  },
};

const TOCModal = () => {
  const intl = useIntl();
  const data = useModalData('TOCModal');
  const mode = data?.tocId || data?.tolId ? 'edit' : 'create';
  // @ts-expect-error
  const tabLeaderOptions = useMemo<SelectOption<Editor.Data.TabLeader>[]>(
    () => [
      { value: 'n', label: intl.formatMessage({ id: 'NONE' }).toLowerCase() },
      ...Object.typedKeys(TAB_LEADER).map((tabLeader) => ({
        value: tabLeader,
        label: TAB_LEADER[tabLeader].labelConstructor(
          intl.formatMessage({ id: TAB_LEADER[tabLeader].labelId }),
        ),
      })),
    ],
    [],
  );
  const headingOptions = useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = [];

    for (let heading = 1; heading <= 6; heading++) {
      options.push({ value: `${heading}`, label: `${intl.formatMessage({ id: 'P' })} ${heading}` });
    }

    return options;
  }, []);
  const [headings, setHeadings] = useState(headingOptions.slice(0, 3));

  const captionOptions = useMemo<SelectOption[]>(() => {
    let customOptions =
      EditorManager.getInstance()
        .getAvailableCaptions()
        ?.reduce<{ value: string; label: string }[]>((acc, item) => {
          acc.push({ value: item, label: item });
          return acc;
        }, []) ?? [];

    const requiredOptions = ['Figure', 'Table', 'Equation'];
    const defaultOptions = customOptions.filter((element) =>
      requiredOptions.includes(element.value),
    );
    customOptions = customOptions.filter((item) => !requiredOptions.includes(item.value));
    return [...defaultOptions, ...customOptions];
  }, []);
  const [caption, setCaption] = useState<SelectOption | null>(captionOptions[0]);

  const [tabLeader, setTabLeader] = useState(tabLeaderOptions[0]);

  const [table, setTable] = useState<'contents' | 'captions'>('contents');

  const [pageNumbers, setPageNumbers] = useState(false);
  const [alignToRight, setAlignToRight] = useState(false);
  const [useContentLinks, setUseContentLinks] = useState(false);
  const [useCaptionLinks, setUseCaptionLinks] = useState(true);
  const [initialValues, setInitialValues] = useState<{
    headings?: TableOfContents.TOCSectionProperties['representingLevels'];
    caption?: string | null;
    tabLeader: string;
    pageNumbers: boolean;
    alignToRight: boolean;
    useContentLinks: boolean;
    useCaptionLinks: boolean;
  }>();

  useEffect(() => {
    if (data && mode === 'create') {
      let templateProperties = null;
      if (data.table === 'captions' && caption) {
        templateProperties =
          EditorManager.getInstance().DataManager?.tableOfContents.getDefaultTolDefinition(
            caption.value,
          ) || null;
      }
      if (data.table === 'contents') {
        templateProperties =
          EditorManager.getInstance().DataManager?.tableOfContents.getDefaultTocDefinition() ||
          null;
      }
      setModalProperties(templateProperties);
    }
  }, [caption, mode, data?.table]);

  useEffect(() => {
    if (data?.blockId) {
      let properties = null;
      if (data.table === 'contents' && data.tocId) {
        properties = EditorManager.getInstance().getPropertiesFromTOCSection(
          data.blockId,
          data.tocId,
        );
      } else if (data.table === 'captions' && data.tolId) {
        properties = EditorManager.getInstance().getPropertiesFromTOLSection(
          data.blockId,
          data.tolId,
        );
      }

      setModalProperties(properties);
      setInitialValues({
        headings: (properties as TableOfContents.TOCSectionProperties)?.representingLevels || [],
        caption: (properties as TableOfContents.TOLSectionProperties)?.label || null,
        tabLeader: properties?.leader || tabLeaderOptions[0],
        pageNumbers: properties?.showPageNumber || false,
        alignToRight: properties?.alignNumberRight || false,
        useContentLinks: properties?.useLink ?? false,
        useCaptionLinks: properties?.useLink ?? true,
      });
    }
    if (data?.table) {
      setTable(data.table);
    }
  }, [data?.blockId, data?.tocId, data?.tolId]);

  const togglePageNumbers = () => setPageNumbers((prev) => !prev);
  const toggleAlignToRight = () => setAlignToRight((prev) => !prev);
  const toggleUseContentLinks = () => setUseContentLinks((prev) => !prev);
  const toggleUseCaptionLinks = () => setUseCaptionLinks((prev) => !prev);

  const valuesChanged = useMemo(() => {
    const headingsValues = headings.map((item) => Number(item.value));
    const compareHeadings = (
      initialHeadings: TableOfContents.TOCSectionProperties['representingLevels'] | undefined,
      currentHeadings: number[],
    ) => {
      if (initialHeadings && initialHeadings?.length > 0) {
        return (
          initialHeadings?.length === currentHeadings?.length &&
          initialHeadings
            .sort()
            .every(
              (element: number | string, index: number) =>
                element === currentHeadings.sort()[index],
            )
        );
      } else return true;
    };
    const isCaptionChanged =
      table !== 'contents' ? (initialValues?.caption || '') !== (caption?.value || '') : false;

    return (
      initialValues?.tabLeader !== tabLeader?.value ||
      !compareHeadings(initialValues?.headings, headingsValues) ||
      isCaptionChanged ||
      initialValues?.pageNumbers !== pageNumbers ||
      initialValues?.alignToRight !== alignToRight ||
      (table !== 'contents'
        ? initialValues?.useCaptionLinks !== useCaptionLinks
        : initialValues?.useContentLinks !== useContentLinks)
    );
  }, [
    initialValues,
    headings,
    table,
    caption,
    tabLeader,
    pageNumbers,
    alignToRight,
    useContentLinks,
    useCaptionLinks,
  ]);

  const setModalProperties = (
    properties: TableOfContents.TOCSectionProperties | TableOfContents.TOLSectionProperties | null,
  ) => {
    if (properties) {
      setAlignToRight(properties.alignNumberRight ?? false);
      setTabLeader(
        tabLeaderOptions.find(({ value }) => properties.leader === value) ?? tabLeaderOptions[0],
      );
      if ('representingLevels' in properties) {
        setHeadings(
          headingOptions.filter(({ value }) => properties.representingLevels.includes(+value)),
        );
      } else {
        setCaption(captionOptions.find(({ label }) => label === properties.label) ?? null);
      }
      setPageNumbers(properties.showPageNumber);
      setUseContentLinks(properties.useLink);
      setUseCaptionLinks(properties.useLink);
    }
  };

  const close = () => {
    closeModal('TOCModal');
  };

  const handleInsert = async () => {
    let result;
    try {
      if (table === 'contents') {
        const options = {
          representingLevels: headings.map((value) => +value.value),
          showPageNumber: pageNumbers,
          alignNumberRight: alignToRight,
          useLink: useContentLinks,
          leader: tabLeader.value,
        };
        if (mode === 'create') {
          EditorManager.getInstance().insertTableOfContents(options);
        } else if (mode === 'edit' && data?.blockId) {
          result = EditorManager.getInstance().updateTOCSection(data.blockId, data.tocId, options);
        }
      } else if (table === 'captions' && caption) {
        const options = {
          label: caption?.value,
          showPageNumber: pageNumbers,
          alignNumberRight: alignToRight,
          useLink: useCaptionLinks,
          leader: tabLeader.value,
        };
        if (mode === 'create') {
          EditorManager.getInstance().insertTableOfLabels(options);
        } else if (mode === 'edit' && data?.blockId) {
          result = EditorManager.getInstance().updateTableOfLabels(
            data.blockId,
            data.tolId,
            options,
          );
        }
      }

      if (result) {
        await result;
        toast({
          variant: 'success',
          title: 'TABLE_OF_CONTENT_TYPE_EDITED_TITLE',
          message: 'TABLE_OF_CONTENT_TYPE_EDITED_MESSAGE',
          titleValues: {
            type: initialValues?.caption ?? 'contents',
          },
          messageValues: {
            type: initialValues?.caption ?? 'contents',
          },
        });
      }
    } catch (error) {
      toast({
        variant: 'error',
        title: 'notifications.genericError.title',
        message: 'notifications.genericError.message',
      });
      Logger.captureException(error);
    }
    close();
  };

  return (
    <Modal open width="67rem" testId="insert-toc" close={close}>
      <Modal.Header>
        <FormattedMessage id={TRANSLATIONS[mode].header} />
      </Modal.Header>
      <Modal.Body>
        {mode === 'create' && (
          <ToggleGroup
            toggles={[
              {
                label: intl.formatMessage({ id: 'editor.menu.insert.tableOfContents' }),
                value: 'contents',
              },
              { label: intl.formatMessage({ id: 'TABLE_OF_CAPTIONS' }), value: 'captions' },
            ]}
            selected={table}
            onToggle={setTable}
            size="m"
            testId="toc"
            fullWidth
          />
        )}
        <div className={styles.root}>
          {table === 'contents' ? (
            <InputField
              label={intl.formatMessage({ id: 'HEADING_LEVELS_TO_BE_DISPLAYED' })}
              size="l"
              style={{ margin: mode === 'create' ? '3rem 0 0' : 0 }}
              testId="toc-displayed-heading-levels-input"
              renderFeedbackIfEmpty={false}
            >
              <Select
                variant="standard"
                size="l"
                value={headings}
                options={headingOptions}
                onChange={(newValue) => setHeadings([...newValue])}
                groupOptionsBySelection
                unselectedGroupLabel={intl.formatMessage({
                  id: 'LIST_OF_HEADING_LEVELS',
                })}
                overflowLabel={(selected) =>
                  `${selected} ${intl
                    .formatMessage(
                      {
                        id: 'SELECTED_OPTIONS',
                      },
                      { total: selected },
                    )
                    .toLowerCase()}`
                }
                selectedGroupLabel={(selected) =>
                  `${intl.formatMessage({
                    id: 'SELECTED_HEADING_LEVELS',
                  })} (${selected})`
                }
                isMulti
                testId="toc-displayed-heading-levels-select"
                useBodyAsPortal
              />
            </InputField>
          ) : (
            <InputField
              label={intl.formatMessage({ id: 'CAPTIONS_TO_INCLUDE' })}
              size="l"
              style={{ margin: mode === 'create' ? '3rem 0 0' : 0 }}
              testId="toc-captions-to-include"
              renderFeedbackIfEmpty={false}
            >
              <Select
                variant="standard"
                size="l"
                value={caption}
                options={captionOptions}
                isClearable
                onChange={setCaption}
                testId="toc-captions-to-include-select"
              />
            </InputField>
          )}
          <InputField
            label={intl.formatMessage({ id: 'TAB_LEADER' })}
            size="l"
            style={{ margin: '3rem 0 0' }}
            testId="toc-tab-leader"
            disabled={!pageNumbers}
            renderFeedbackIfEmpty={false}
          >
            <Select
              variant="standard"
              size="l"
              value={tabLeader}
              options={tabLeaderOptions}
              onChange={setTabLeader}
              isClearable={false}
              disabled={!pageNumbers}
              testId="toc-tab-leader-select"
              useBodyAsPortal
            />
          </InputField>
          <div className={styles.settings}>
            <Checkbox
              size="s"
              checked={pageNumbers}
              onChange={togglePageNumbers}
              testId="toc-show-page-numbers"
            >
              <FormattedMessage id="SHOW_PAGE_NUMBERS" />
            </Checkbox>
            <Checkbox
              size="s"
              checked={alignToRight}
              onChange={toggleAlignToRight}
              testId="toc-align-numbers-right"
              disabled={!pageNumbers}
            >
              <FormattedMessage id="ALIGN_PAGE_NUMBERS_TO_RIGHT" />
            </Checkbox>
            {table === 'contents' ? (
              <Checkbox
                size="s"
                checked={useContentLinks}
                onChange={toggleUseContentLinks}
                testId="toc-use-links"
              >
                <FormattedMessage id="INSERT_AS_A_HYPERLINK" />
              </Checkbox>
            ) : (
              <Checkbox
                size="s"
                checked={useCaptionLinks}
                onChange={toggleUseCaptionLinks}
                testId="toc-use-links"
              >
                <FormattedMessage id="INSERT_AS_A_HYPERLINK" />
              </Checkbox>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="standard" size="m" onClick={close} testId="insert-toc-modal-close">
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          variant="primary"
          onClick={handleInsert}
          style={{ margin: '0 0 0 1rem' }}
          testId="insert-toc-modal-submit"
          disabled={
            (table === 'captions' && !caption) ||
            (table === 'contents' && headings.length === 0) ||
            !valuesChanged
          }
        >
          <FormattedMessage id={TRANSLATIONS[mode].submit} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TOCModal;
